import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useFirmList } from '../../api/users'
import SydModal from '../../components/commonDesign/SydModal'
import SydModalActions from '../../components/commonDesign/SydModal/SydModalActions'
import SydButton from '../../components/commonDesign/Button'
import SydAutocomplete from '../../components/commonDesign/SydAutocomplete'
import SydInput from '../../components/commonDesign/SydInput'
import SydLabel from '../../components/commonDesign/SydLabel'

const FIRM_PLACEHOLDERS = [
  'Search or select a firm...',
  'Choose a firm to switch to...',
  'Where we going?'
]

function FirmSelector ({ open, onClose, onConfirm, enabled }) {
  const autocompleteRef = useRef(null)
  const formRef = useRef(null)
  const [placeholder] = useState(() => FIRM_PLACEHOLDERS[Math.floor(Math.random() * FIRM_PLACEHOLDERS.length)])
  const [selectedFirm, setSelectedFirm] = useState(null)
  const [manualFirmId, setManualFirmId] = useState('')
  const [searchText, setSearchText] = useState('')
  const [debouncedSearchText, setDebouncedSearchText] = useState('')
  const [error, setError] = useState(null)
  const { data: firmList, isLoading, isFetching, isError } = useFirmList({ enabled: enabled && open })
  const hasFirms = firmList?.length > 0
  const isActuallyLoading = isLoading && isFetching
  const showManualEntry = !isActuallyLoading && !hasFirms

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchText(searchText), 400)
    return () => clearTimeout(timer)
  }, [searchText])

  const firmOptions = useMemo(() => {
    if (!firmList?.length) return []
    const all = [...firmList]
      .sort((a, b) => a.firmId - b.firmId)
      .map((f) => ({
        value: f.firmId,
        label: `${f.firmId} - ${f.name}`
      }))
    if (!debouncedSearchText) return all
    const term = debouncedSearchText.toLowerCase()
    return all.filter((opt) => opt.label.toLowerCase().includes(term))
  }, [firmList, debouncedSearchText])

  const focusSubmit = useCallback(() => {
    setTimeout(() => {
      const btn = formRef.current?.querySelector('button[type="submit"]')
      if (btn) btn.focus()
    }, 50)
  }, [])

  useEffect(() => {
    if (firmOptions.length === 1 && searchText && searchText.length >= 3) {
      setSelectedFirm(firmOptions[0])
      const input = autocompleteRef.current?.querySelector('input')
      if (input) input.blur()
      focusSubmit()
    }
  }, [firmOptions, searchText, focusSubmit])

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        const input = autocompleteRef.current?.querySelector('input')
        if (input) input.focus()
      }, 250)
    }
  }, [open])

  const reset = useCallback(() => {
    setSelectedFirm(null)
    setManualFirmId('')
    setSearchText('')
    setDebouncedSearchText('')
    setError(null)
  }, [])

  const handleKeyDown = useCallback((e) => {
    if ((e.key === 'Backspace' || e.key === 'Delete') && selectedFirm) {
      const input = autocompleteRef.current?.querySelector('input')
      if (document.activeElement !== input) {
        e.preventDefault()
        reset()
        setTimeout(() => {
          const el = autocompleteRef.current?.querySelector('input')
          if (el) el.focus()
        }, 50)
      }
    }
  }, [selectedFirm, reset])

  const handleClose = useCallback(() => {
    reset()
    onClose()
  }, [reset, onClose])

  const handleSubmit = useCallback((e) => {
    e.preventDefault()
    const toFirmId = selectedFirm?.value || (manualFirmId ? Number(manualFirmId) : null)
    if (!toFirmId) {
      setError(showManualEntry ? 'Please enter a firm number' : 'Please select a firm')
      return
    }
    reset()
    onConfirm(toFirmId)
  }, [selectedFirm, manualFirmId, showManualEntry, reset, onConfirm])

  return (
    <SydModal
      open={open}
      onClose={handleClose}
      title='Change Firm Instance'
      size='xsmall'
    >
      <form ref={formRef} onSubmit={handleSubmit} onKeyDown={handleKeyDown} style={{ display: 'flex', flexDirection: 'column' }}>
        {showManualEntry && (
          <div
            style={{
              padding: '12px 16px',
              borderRadius: 'var(--border-radius-sm)',
              backgroundColor: 'var(--color-warning-100)',
              color: 'var(--color-warning-900)',
              fontSize: '14px',
              marginBottom: '16px'
            }}
          >
            {isError
              ? 'Unable to load firm list. You can still switch by entering a firm number below.'
              : 'No firms are currently available. You can still switch by entering a firm number below.'}
          </div>
        )}
        <div className='flex flex-col gap-3 mb-4'>
          {showManualEntry ? (
            <SydLabel label='Firm Number' error={error}>
              <SydInput
                placeholder='Enter firm number...'
                size='sm'
                type='number'
                value={manualFirmId}
                onChange={(e) => {
                  setManualFirmId(e.target.value)
                  setError(null)
                }}
                autoFocus
              />
            </SydLabel>
          ) : (
            <SydLabel label='Firm' error={error}>
              <div ref={autocompleteRef}>
                <SydAutocomplete
                  placeholder={placeholder}
                  size='sm'
                  isLoading={isActuallyLoading}
                  value={selectedFirm}
                  options={firmOptions}
                  onChange={(e, value) => {
                    setSelectedFirm(value)
                    setError(null)
                    if (value) {
                      focusSubmit()
                    } else {
                      setSearchText('')
                      setDebouncedSearchText('')
                    }
                  }}
                  onInputChange={(e, value) => {
                    setSearchText(value)
                    if (!value) {
                      setDebouncedSearchText('')
                      setSelectedFirm(null)
                    } else if (selectedFirm && value !== selectedFirm.label) {
                      setSelectedFirm(null)
                    }
                  }}
                />
              </div>
            </SydLabel>
          )}
        </div>
        <SydModalActions style={{ marginTop: 'auto' }}>
          <SydButton
            variant='secondary'
            type='button'
            onClick={handleClose}
          >
            Cancel
          </SydButton>
          <SydButton
            variant='primary'
            type='submit'
            disabled={showManualEntry ? !manualFirmId : !selectedFirm}
          >
            Change Firm
          </SydButton>
        </SydModalActions>
      </form>
    </SydModal>
  )
}

FirmSelector.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  enabled: PropTypes.bool
}

export default FirmSelector
